// BHP-1062: Linked Helper VPS detail. Same body as the generic page, plus the
// installation state and the NoMachine connection file this product needs.
//
// The label editor is deliberately NOT ported. It writes hostname and labels
// through the legacy endpoint, which forwards them to Hetzner. Backend2.0 has
// no equivalent: `PATCH /api/vps/{uuid}` changes our own row, and the sync
// (BHP-1067) overwrites it on the next tick, so the edit would silently undo
// itself. Same reason the "Delete Server" button is gone — `DELETE /api/vps`
// drops our row while the machine keeps running. Both need an endpoint that
// mutates Hetzner; recorded as a follow-up on BHP-1062.
import { useParams } from 'react-router-dom';
import VpsServerDetail from '../../features/vps/ui/VpsServerDetail';
import InstallationState from '../../features/vps/ui/InstallationState';
import type { VpsServerState } from '../../features/vps/domain/vpsServerState';
import { nxConnectionFile } from '../../features/vps/domain/nxConnectionFile';

function fileNameFor(hostname: string): string {
  return hostname
    .trim()
    .replaceAll(' ', '-')
    .replace(/[^a-z0-9-]/gi, '')
    .replace(/-+/gi, '-')
    .toLowerCase();
}

function downloadConfig(hostname: string, ip: string): void {
  const blob = new Blob([nxConnectionFile(ip)], {
    type: 'text/plain'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.download = `${fileNameFor(hostname)}.nxs`;
  link.href = url;
  link.click();
  // The old version never released the object URL, so every download leaked one
  // for the lifetime of the tab.
  URL.revokeObjectURL(url);
}

function ConnectionFile({
  hostname,
  state
}: {
  hostname: string;
  state: VpsServerState | undefined;
}): JSX.Element {
  const ip = state?.publicIpv4 ?? null;

  return (
    <div className="rounded-xl border border-solid border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="mb-2 text-sm font-semibold text-slate-800">
        Remote connection
      </h3>
      {ip === null ? (
        <p className="m-0 text-sm text-slate-500">
          A connection file needs the server&apos;s IPv4 address, which is not
          available right now.
        </p>
      ) : (
        <button
          type="button"
          onClick={() => {
            downloadConfig(hostname, ip);
          }}
          className="rounded-md border border-solid border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Download connection file
        </button>
      )}
    </div>
  );
}

export default function LinkedHelperVpsDetails(): JSX.Element {
  const { uuid } = useParams<{ uuid: string }>();

  return (
    <VpsServerDetail
      uuid={uuid ?? ''}
      overviewPath="/vps/linked-helper/overview"
      overviewLabel="Back to Linked Helper servers"
      extra={(vps, state) => (
        <>
          <InstallationState labels={vps.labels} />
          <ConnectionFile hostname={vps.hostname} state={state} />
        </>
      )}
    />
  );
}
